import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Palette, Plus, Edit2, Trash2, Copy, Star, Image, Eye, RefreshCw, X, Package, Check, ExternalLink, ArrowLeft
} from 'lucide-react';
import { AdminLayout } from '../components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminAtmospheresPage() {
  const [atmospheres, setAtmospheres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [defaultId, setDefaultId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingAtm, setEditingAtm] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);
  
  // Products selection state
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedAtmosphere, setSelectedAtmosphere] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  
  // Details/History modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsAtmosphere, setDetailsAtmosphere] = useState(null);
  const [atmosphereProducts, setAtmosphereProducts] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    base_prompt: '',
    additional_details: '',
    target_formats: ['product_photo', 'slide'],
    is_default: false,
    is_active: true,
  });

  const getToken = () => localStorage.getItem('admin_token');

  useEffect(() => {
    fetchAtmospheres();
  }, []);

  const fetchAtmospheres = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/atmospheres?include_inactive=true`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (response.ok) {
        const data = await response.json();
        setAtmospheres(data.atmospheres);
        setDefaultId(data.default_id);
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingAtm 
        ? `${API_URL}/api/admin/atmospheres/${editingAtm.id}`
        : `${API_URL}/api/admin/atmospheres`;
      
      const response = await fetch(url, {
        method: editingAtm ? 'PATCH' : 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail);
      
      toast.success(editingAtm ? 'Atmosphère mise à jour' : 'Atmosphère créée');
      setShowModal(false);
      setEditingAtm(null);
      resetForm();
      fetchAtmospheres();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer cette atmosphère ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/atmospheres/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail);
      
      toast.success('Atmosphère supprimée');
      fetchAtmospheres();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/atmospheres/${id}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      
      if (response.ok) {
        toast.success('Atmosphère dupliquée');
        fetchAtmospheres();
      }
    } catch (error) {
      toast.error('Erreur de duplication');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/atmospheres/${id}/set-default`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      
      if (response.ok) {
        toast.success('Atmosphère par défaut mise à jour');
        fetchAtmospheres();
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handlePreview = async (id) => {
    setPreviewLoading(id);
    try {
      const response = await fetch(`${API_URL}/api/admin/atmospheres/${id}/preview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(`Aperçu généré (${data.mode})`);
        fetchAtmospheres();
      } else {
        throw new Error(data.detail);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setPreviewLoading(null);
    }
  };

  // Fetch products for selection
  const fetchProducts = async () => {
    setProductsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/products?limit=100`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data.products || []);
      }
    } catch (error) {
      toast.error('Erreur de chargement des produits');
    } finally {
      setProductsLoading(false);
    }
  };

  // Open products selection modal
  const openProductsModal = async (atm) => {
    setSelectedAtmosphere(atm);
    setSelectedProductIds(atm.product_ids || []);
    await fetchProducts();
    setShowProductsModal(true);
  };

  // Toggle product selection
  const toggleProductSelection = (productId) => {
    setSelectedProductIds(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Save products to atmosphere
  const saveProductsToAtmosphere = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/atmospheres/${selectedAtmosphere.id}/products`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ product_ids: selectedProductIds }),
      });
      
      if (response.ok) {
        toast.success(`${selectedProductIds.length} produits associés à l'atmosphère`);
        setShowProductsModal(false);
        fetchAtmospheres();
      } else {
        const data = await response.json();
        throw new Error(data.detail);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Open details/history modal
  const openDetailsModal = async (atm) => {
    setDetailsAtmosphere(atm);
    setDetailsLoading(true);
    setShowDetailsModal(true);
    
    try {
      // Fetch products associated with this atmosphere
      const response = await fetch(`${API_URL}/api/admin/atmospheres/${atm.id}/products`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setAtmosphereProducts(data.products || []);
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setDetailsLoading(false);
    }
  };

  // Apply atmosphere to selected products (generate images)
  const applyAtmosphereToProducts = async (atmId) => {
    const atm = atmospheres.find(a => a.id === atmId);
    if (!atm?.product_ids?.length) {
      toast.error('Aucun produit associé à cette atmosphère');
      return;
    }
    
    toast.info(`Génération d'images pour ${atm.product_ids.length} produits en cours...`, {
      duration: 5000,
    });
    
    try {
      const response = await fetch(`${API_URL}/api/admin/atmospheres/${atmId}/apply-to-products`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(`✅ ${data.processed_count} images générées (mode ${data.mode})`);
        // Rafraîchir les atmosphères pour voir les mises à jour
        fetchAtmospheres();
      } else {
        throw new Error(data.detail);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openEditModal = (atm) => {
    setEditingAtm(atm);
    setFormData({
      name: atm.name,
      base_prompt: atm.base_prompt,
      additional_details: atm.additional_details || '',
      target_formats: atm.target_formats || [],
      is_default: atm.is_default,
      is_active: atm.is_active,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      base_prompt: '',
      additional_details: '',
      target_formats: ['product_photo', 'slide'],
      is_default: false,
      is_active: true,
    });
  };

  const formatOptions = [
    { id: 'product_photo', label: 'Photo produit' },
    { id: 'slide', label: 'Slide marketing' },
    { id: 'social_ad', label: 'Pub sociale' },
    { id: 'video', label: 'Vidéo' },
  ];

  return (
    <AdminLayout title="Atmosphères" subtitle="Presets de styles visuels pour la génération IA">
      <div className="max-w-6xl mx-auto" data-testid="admin-atmospheres-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <Palette className="w-8 h-8 text-[#C0000C]" />
              Atmosphères
            </h1>
            <p className="text-gray-400">Presets de styles visuels pour la génération IA</p>
          </div>
          <Button
            onClick={() => { resetForm(); setEditingAtm(null); setShowModal(true); }}
            className="bg-[#C0000C] hover:bg-[#A0000A] text-white"
            data-testid="create-atmosphere-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle atmosphère
          </Button>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="text-center py-20 text-gray-200">Chargement...</div>
        ) : atmospheres.length === 0 ? (
          <div className="text-center py-20">
            <Palette className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <p className="text-white">Aucune atmosphère créée</p>
            <p className="text-zinc-600 text-sm">Créez votre première atmosphère pour générer des visuels IA</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {atmospheres.map(atm => (
              <div
                key={atm.id}
                className={`bg-zinc-800/30 border rounded-xl overflow-hidden ${
                  atm.is_default ? 'border-[#C0000C]' : 'border-zinc-700/50'
                } ${!atm.is_active ? 'opacity-50' : ''}`}
                data-testid={`atmosphere-${atm.id}`}
              >
                {/* Preview Image - Cliquable */}
                <div 
                  className="aspect-video bg-zinc-900 relative cursor-pointer group"
                  onClick={() => openDetailsModal(atm)}
                >
                  {atm.last_render_url ? (
                    <img
                      src={`${API_URL}${atm.last_render_url}`}
                      alt={atm.name}
                      className="w-full h-full object-cover group-hover:opacity-80 transition-opacity"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center group-hover:bg-zinc-800 transition-colors">
                      <Image className="w-12 h-12 text-zinc-700" />
                    </div>
                  )}
                  {atm.is_default && (
                    <div className="absolute top-2 right-2 bg-[#C0000C] text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      Par défaut
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 
                    className="text-white font-bold mb-1 cursor-pointer hover:text-[#C0000C] transition-colors"
                    onClick={() => openDetailsModal(atm)}
                  >
                    {atm.name}
                  </h3>
                  <p className="text-gray-200 text-sm line-clamp-2 mb-3">{atm.base_prompt}</p>
                  
                  <div className="flex items-center gap-2 text-xs text-gray-200 mb-4">
                    <span>{atm.products_count || atm.product_ids?.length || 0} produit(s)</span>
                    <span>•</span>
                    <span>{atm.target_formats?.length || 0} format(s)</span>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openProductsModal(atm)}
                      className="border-blue-600 text-blue-400 hover:bg-blue-600/20"
                      title="Sélectionner les produits"
                    >
                      <Package className="w-4 h-4" />
                      <span className="ml-1 text-xs">{atm.product_ids?.length || 0}</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePreview(atm.id)}
                      disabled={previewLoading === atm.id}
                      className="border-zinc-700 text-white hover:text-white"
                    >
                      {previewLoading === atm.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditModal(atm)}
                      className="border-zinc-700 text-white hover:text-white"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDuplicate(atm.id)}
                      className="border-zinc-700 text-white hover:text-white"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    {!atm.is_default && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSetDefault(atm.id)}
                        className="border-yellow-600 text-yellow-500 hover:bg-yellow-600/20"
                      >
                        <Star className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(atm.id)}
                      className="border-red-600 text-red-500 hover:bg-red-600/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Apply to products button */}
                  {atm.product_ids?.length > 0 && (
                    <Button
                      size="sm"
                      onClick={() => applyAtmosphereToProducts(atm.id)}
                      className="w-full mt-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                    >
                      <Image className="w-4 h-4 mr-2" />
                      Générer visuels pour {atm.product_ids.length} produit(s)
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal Create/Edit */}
        {showModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                <h2 className="text-xl font-bold text-white">
                  {editingAtm ? 'Modifier l\'atmosphère' : 'Nouvelle atmosphère'}
                </h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowModal(false)}
                  className="text-white hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <Label className="text-white">Nom *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="Ex: Tokyo Streetwear Night"
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    data-testid="atm-name-input"
                  />
                </div>

                <div>
                  <Label className="text-white">Prompt de base *</Label>
                  <Textarea
                    value={formData.base_prompt}
                    onChange={(e) => setFormData({...formData, base_prompt: e.target.value})}
                    required
                    placeholder="Décrivez le style visuel souhaité..."
                    rows={4}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1 resize-none"
                    data-testid="atm-prompt-input"
                  />
                </div>

                <div>
                  <Label className="text-white">Précisions supplémentaires</Label>
                  <Textarea
                    value={formData.additional_details}
                    onChange={(e) => setFormData({...formData, additional_details: e.target.value})}
                    placeholder="Détails additionnels, couleurs, ambiance..."
                    rows={2}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1 resize-none"
                  />
                </div>

                <div>
                  <Label className="text-white mb-2 block">Formats cibles</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {formatOptions.map(opt => (
                      <label key={opt.id} className="flex items-center gap-2 text-white text-sm cursor-pointer">
                        <Checkbox
                          checked={formData.target_formats.includes(opt.id)}
                          onCheckedChange={(checked) => {
                            const newFormats = checked
                              ? [...formData.target_formats, opt.id]
                              : formData.target_formats.filter(f => f !== opt.id);
                            setFormData({...formData, target_formats: newFormats});
                          }}
                          className="border-zinc-600"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <Label className="text-white">Actif</Label>
                    <p className="text-gray-200 text-xs">Visible dans les sélecteurs</p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowModal(false)}
                    className="flex-1 border-zinc-700 text-white"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-[#C0000C] hover:bg-[#A0000A] text-white"
                    data-testid="atm-submit-btn"
                  >
                    {editingAtm ? 'Mettre à jour' : 'Créer'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Products Selection */}
        {showProductsModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                <div>
                  <h2 className="text-xl font-bold text-white">Sélectionner les produits</h2>
                  <p className="text-sm text-gray-400">Atmosphère : {selectedAtmosphere?.name}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowProductsModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                {productsLoading ? (
                  <div className="text-center py-10 text-gray-400">Chargement des produits...</div>
                ) : products.length === 0 ? (
                  <div className="text-center py-10 text-gray-400">Aucun produit disponible</div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm text-gray-400">{selectedProductIds.length} produit(s) sélectionné(s)</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (selectedProductIds.length === products.length) {
                            setSelectedProductIds([]);
                          } else {
                            setSelectedProductIds(products.map(p => p.id));
                          }
                        }}
                        className="text-xs border-zinc-700 text-white"
                      >
                        {selectedProductIds.length === products.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                      </Button>
                    </div>
                    
                    {products.map(product => (
                      <div
                        key={product.id}
                        onClick={() => toggleProductSelection(product.id)}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedProductIds.includes(product.id) 
                            ? 'bg-blue-600/20 border border-blue-500' 
                            : 'bg-zinc-800 border border-zinc-700 hover:bg-zinc-700'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                          selectedProductIds.includes(product.id) 
                            ? 'bg-blue-600 border-blue-600' 
                            : 'border-zinc-600'
                        }`}>
                          {selectedProductIds.includes(product.id) && (
                            <Check className="w-3 h-3 text-white" />
                          )}
                        </div>
                        
                        <div className="w-12 h-12 bg-zinc-900 rounded overflow-hidden flex-shrink-0">
                          {product.images?.[0] ? (
                            <img 
                              src={product.images[0]} 
                              alt={product.name?.fr || ''} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-5 h-5 text-zinc-600" />
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium text-sm truncate">
                            {product.name?.fr || 'Sans nom'}
                          </p>
                          <p className="text-gray-400 text-xs">
                            {product.pricing?.prices?.ILS || product.price?.amount || 0} ILS
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="p-4 border-t border-zinc-800 flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowProductsModal(false)}
                  className="flex-1 border-zinc-700 text-white"
                >
                  Annuler
                </Button>
                <Button
                  onClick={saveProductsToAtmosphere}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Enregistrer ({selectedProductIds.length})
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Modal Details/History */}
        {showDetailsModal && detailsAtmosphere && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                <div>
                  <h2 className="text-xl font-bold text-white">{detailsAtmosphere.name}</h2>
                  <p className="text-sm text-gray-400">Détails et historique de génération</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowDetailsModal(false);
                      openEditModal(detailsAtmosphere);
                    }}
                    className="border-zinc-600 text-white"
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Modifier
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowDetailsModal(false)}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {/* Prompt */}
                <div>
                  <Label className="text-white text-sm font-semibold">Prompt de base</Label>
                  <p className="mt-1 text-gray-300 text-sm bg-zinc-800/50 p-3 rounded-lg">
                    {detailsAtmosphere.base_prompt}
                  </p>
                  {detailsAtmosphere.additional_details && (
                    <>
                      <Label className="text-white text-sm font-semibold mt-3 block">Détails additionnels</Label>
                      <p className="mt-1 text-gray-400 text-sm">{detailsAtmosphere.additional_details}</p>
                    </>
                  )}
                </div>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-zinc-800/50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-white">{detailsAtmosphere.product_ids?.length || 0}</p>
                    <p className="text-xs text-gray-400">Produits associés</p>
                  </div>
                  <div className="bg-zinc-800/50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-white">{detailsAtmosphere.target_formats?.length || 0}</p>
                    <p className="text-xs text-gray-400">Formats cibles</p>
                  </div>
                  <div className="bg-zinc-800/50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-white">{detailsAtmosphere.is_active ? '✓' : '✗'}</p>
                    <p className="text-xs text-gray-400">{detailsAtmosphere.is_active ? 'Actif' : 'Inactif'}</p>
                  </div>
                </div>
                
                {/* Preview Image */}
                {detailsAtmosphere.last_render_url && (
                  <div>
                    <Label className="text-white text-sm font-semibold">Aperçu généré</Label>
                    <img
                      src={`${API_URL}${detailsAtmosphere.last_render_url}`}
                      alt="Aperçu"
                      className="mt-2 w-full max-w-md rounded-lg border border-zinc-700"
                    />
                  </div>
                )}
                
                {/* Products with AI Images */}
                <div>
                  <Label className="text-white text-sm font-semibold mb-3 block">
                    Produits associés et images générées
                  </Label>
                  
                  {detailsLoading ? (
                    <div className="text-center py-8 text-gray-400">Chargement...</div>
                  ) : atmosphereProducts.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Package className="w-10 h-10 mx-auto mb-2 opacity-50" />
                      <p>Aucun produit associé</p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setShowDetailsModal(false);
                          openProductsModal(detailsAtmosphere);
                        }}
                        className="mt-3 border-blue-500 text-blue-400"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Associer des produits
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {atmosphereProducts.map(product => (
                        <div key={product.id} className="flex items-center gap-4 p-3 bg-zinc-800/50 rounded-lg hover:bg-zinc-800 transition-colors">
                          {/* Product Image */}
                          <div className="w-16 h-16 bg-zinc-900 rounded overflow-hidden flex-shrink-0">
                            {product.images?.[0] ? (
                              <img 
                                src={product.images[0]} 
                                alt={product.name?.fr || ''} 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-6 h-6 text-zinc-600" />
                              </div>
                            )}
                          </div>
                          
                          {/* Product Info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{product.name?.fr || 'Sans nom'}</p>
                            <p className="text-gray-400 text-sm">
                              {product.pricing?.prices?.ILS || product.price?.amount || 0} ILS
                            </p>
                            {/* Lien vers le produit */}
                            <a 
                              href={`/admin/sourcing?product=${product.id}`}
                              className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1 mt-1"
                              onClick={(e) => {
                                e.preventDefault();
                                setShowDetailsModal(false);
                                window.location.href = `/admin/sourcing?product=${product.id}`;
                              }}
                            >
                              <ExternalLink className="w-3 h-3" />
                              Voir le produit
                            </a>
                          </div>
                          
                          {/* AI Generated Images (if any) */}
                          <div className="flex gap-1 flex-wrap justify-end">
                            {product.ai_generated_images?.filter(img => img.atmosphere_id === detailsAtmosphere.id).slice(0, 3).map((img, idx) => (
                              <a 
                                key={idx}
                                href={`${API_URL}${img.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                              >
                                <img
                                  src={`${API_URL}${img.url}`}
                                  alt="AI"
                                  className="w-12 h-12 object-cover rounded border border-purple-500/50 hover:border-purple-400 transition-colors cursor-pointer"
                                  title={`Cliquez pour voir en grand - Généré le ${img.generated_at}`}
                                />
                              </a>
                            ))}
                            {!product.ai_generated_images?.filter(img => img.atmosphere_id === detailsAtmosphere.id).length && (
                              <span className="text-xs text-gray-500 italic">Pas encore d'images</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Dates */}
                <div className="text-xs text-gray-500 pt-4 border-t border-zinc-800">
                  <p>Créée le: {detailsAtmosphere.created_at ? new Date(detailsAtmosphere.created_at).toLocaleString('fr-FR') : 'N/A'}</p>
                  <p>Modifiée le: {detailsAtmosphere.updated_at ? new Date(detailsAtmosphere.updated_at).toLocaleString('fr-FR') : 'N/A'}</p>
                </div>
              </div>
              
              <div className="p-4 border-t border-zinc-800 flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDetailsModal(false);
                    openProductsModal(detailsAtmosphere);
                  }}
                  className="flex-1 border-blue-500 text-blue-400 hover:bg-blue-600/20"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Gérer les produits
                </Button>
                <Button
                  onClick={() => {
                    applyAtmosphereToProducts(detailsAtmosphere.id);
                    // Refresh after a short delay
                    setTimeout(() => openDetailsModal(detailsAtmosphere), 2000);
                  }}
                  disabled={!detailsAtmosphere.product_ids?.length}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                >
                  <Image className="w-4 h-4 mr-2" />
                  Générer les images
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminAtmospheresPage;
